import React from "react";
import { Upload, Image as ImageIcon } from "lucide-react";

/**
 * MotilitaForm — render strutturato dei blocchi ortottica.
 * Ogni blocco è gestito tramite il dict `specialized` su Visita.jsx (chiave = blockKey).
 * Tutti i campi sono compatti, chip/radio/select dove possibile, e textarea libera per le note.
 *
 * Props:
 *  - specialized: dict serializzabile
 *  - setSpecialized: setter (oggetto deep merge)
 *  - showBlock: (k) => boolean — visibilità da modello
 *  - readOnly: boolean
 *  - onAttachGaze9: (file) => void — upload foto Gaze9 (usa lo stesso pipeline diagnostiche)
 *  - onAnalyzeGaze9: () => void — placeholder per analisi IA (opzionale)
 *  - gaze9Files: array di allegati gaze9 visualizzati
 */
const ALIGN_OPTIONS = [
  { v: "ortotropico", l: "Ortotropico" },
  { v: "esotropia", l: "Esotropia (ET)" },
  { v: "exotropia", l: "Exotropia (XT)" },
  { v: "ipertropia", l: "Ipertropia (HT)" },
  { v: "ipotropia", l: "Ipotropia (HoT)" },
  { v: "altro", l: "Altro…" },
];

const WORTH_OPTIONS = [
  { v: "fusione4", l: "Fusione (4 luci)" },
  { v: "diplopia5", l: "Diplopia (5 luci)" },
  { v: "soppr_od", l: "Soppressione OD (2 rosse)" },
  { v: "soppr_os", l: "Soppressione OS (3 verdi)" },
];

const LANG_OPTIONS = [
  { v: "positivo", l: "Positivo" },
  { v: "negativo", l: "Negativo" },
  { v: "non_eseguibile", l: "Non eseguibile" },
];

const STEREO_OPTIONS = [
  { v: "presente", l: "Presente" },
  { v: "assente", l: "Assente" },
  { v: "non_valutabile", l: "Non valutabile" },
];

const VERSION_VALUES = [
  { v: "N", l: "Norm" },
  { v: "-1", l: "-1" },
  { v: "-2", l: "-2" },
  { v: "-3", l: "-3" },
  { v: "-4", l: "Ass." },
  { v: "+1", l: "+1" },
  { v: "+2", l: "+2" },
  { v: "+3", l: "+3" },
];

const VERSION_DIRS = [
  { k: "adduzione", l: "Adduzione" },
  { k: "abduzione", l: "Abduzione" },
  { k: "elevazione", l: "Elevazione" },
  { k: "depressione", l: "Depressione" },
];

const GRID9 = [
  ["alto_dx", "Alto DX"], ["alto_centro", "Alto"], ["alto_sx", "Alto SX"],
  ["centro_dx", "Centro DX"], ["primaria", "Primaria"], ["centro_sx", "Centro SX"],
  ["basso_dx", "Basso DX"], ["basso_centro", "Basso"], ["basso_sx", "Basso SX"],
];

function getNested(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}
function setNested(obj, path, val) {
  const keys = path.split(".");
  const out = { ...(obj || {}) };
  let cur = out;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = { ...(cur[k] || {}) };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = val;
  return out;
}

function ChipGroup({ options, value, onChange, disabled, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            disabled={disabled}
            onClick={() => onChange(active ? "" : o.v)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold border-2 transition ${active ? "bg-brand-azure text-white border-brand-azure" : "bg-white text-slate-600 border-[#E2E8F0] hover:border-brand-azure"} disabled:opacity-60`}
            data-testid={`${testid}-${o.v}`}
          >
            {o.l}
          </button>
        );
      })}
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid={testid}>
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Input({ value, onChange, disabled, placeholder, testid, type = "text", className = "" }) {
  return (
    <input
      type={type}
      value={value || ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className={`px-2 py-1.5 rounded border border-[#E2E8F0] focus:outline-none focus:border-brand-azure disabled:bg-slate-50 text-xs ${className}`}
    />
  );
}

export default function MotilitaForm({ specialized, setSpecialized, showBlock, readOnly, onAttachGaze9, gaze9Files }) {
  const v = specialized || {};
  const set = (path, val) => setSpecialized(setNested(v, path, val));

  const blocks = [
    "allineamento", "angolo_pl_pv", "cover_test", "krimsky", "irvine",
    "motilita_9_posizioni", "versioni_duzioni", "forie_tropie",
    "convergenza", "accomodazione",
    "stereopsi", "luci_worth", "vetro_rosso",
    "bielschowsky", "doll_head", "posizione_capo",
    "schermo_hess", "gaze9_foto",
    "diplopia", "nistagmo", "ptosi", "torsione",
  ];
  const visible = blocks.filter((k) => showBlock(k));
  if (visible.length === 0) return null;

  return (
    <div className="bg-brand-azure/5 border-2 border-brand-azure/30 rounded-xl p-4 mb-4" data-testid="motilita-module">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-1 h-5 bg-brand-azure rounded-full"></span>
        <h3 className="font-extrabold text-brand-navy text-base">Modulo Ortottica / Motilità oculare</h3>
        <span className="text-[10px] uppercase tracking-wider bg-brand-azure/10 text-brand-azure px-2 py-0.5 rounded font-bold">{visible.length} sezioni</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* ALLINEAMENTO */}
        {showBlock("allineamento") && (
          <Section title="Allineamento" testid="mot-allineamento">
            <ChipGroup
              options={ALIGN_OPTIONS}
              value={v?.allineamento?.tipo}
              onChange={(val) => set("allineamento.tipo", val)}
              disabled={readOnly}
              testid="align-chips"
            />
            {v?.allineamento?.tipo === "altro" && (
              <Input value={v?.allineamento?.altro} onChange={(x) => set("allineamento.altro", x)} disabled={readOnly} placeholder="Specifica…" testid="align-altro" className="w-full" />
            )}
            <textarea
              rows={2}
              disabled={readOnly}
              value={v?.allineamento?.note || ""}
              onChange={(e) => set("allineamento.note", e.target.value)}
              placeholder="Note allineamento…"
              className="w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs disabled:bg-slate-50"
              data-testid="align-note"
            />
          </Section>
        )}

        {/* ANGOLO PL/PV con/senza correzione */}
        {showBlock("angolo_pl_pv") && (
          <Section title="Angolo di deviazione (PL / PV)" testid="mot-angolo">
            <div className="grid grid-cols-2 gap-2">
              <Input value={v?.angolo?.pl_sc} onChange={(x) => set("angolo.pl_sc", x)} disabled={readOnly} placeholder="PL senza corr (Δ)" testid="angolo-pl-sc" />
              <Input value={v?.angolo?.pl_cc} onChange={(x) => set("angolo.pl_cc", x)} disabled={readOnly} placeholder="PL con corr (Δ)" testid="angolo-pl-cc" />
              <Input value={v?.angolo?.pv_sc} onChange={(x) => set("angolo.pv_sc", x)} disabled={readOnly} placeholder="PV senza corr (Δ)" testid="angolo-pv-sc" />
              <Input value={v?.angolo?.pv_cc} onChange={(x) => set("angolo.pv_cc", x)} disabled={readOnly} placeholder="PV con corr (Δ)" testid="angolo-pv-cc" />
            </div>
          </Section>
        )}

        {/* COVER TEST */}
        {showBlock("cover_test") && (
          <Section title="Cover test" testid="mot-cover">
            <div className="grid grid-cols-2 gap-2">
              <Input value={v?.cover?.lontano_sc} onChange={(x) => set("cover.lontano_sc", x)} disabled={readOnly} placeholder="Lontano s/c" testid="cover-lontano-sc" />
              <Input value={v?.cover?.lontano_cc} onChange={(x) => set("cover.lontano_cc", x)} disabled={readOnly} placeholder="Lontano c/c" testid="cover-lontano-cc" />
              <Input value={v?.cover?.vicino_sc} onChange={(x) => set("cover.vicino_sc", x)} disabled={readOnly} placeholder="Vicino s/c" testid="cover-vicino-sc" />
              <Input value={v?.cover?.vicino_cc} onChange={(x) => set("cover.vicino_cc", x)} disabled={readOnly} placeholder="Vicino c/c" testid="cover-vicino-cc" />
            </div>
            <Input value={v?.cover?.alternato} onChange={(x) => set("cover.alternato", x)} disabled={readOnly} placeholder="Alternate cover (Δ)" testid="cover-alternato" className="w-full" />
            <Input value={v?.cover?.uncover} onChange={(x) => set("cover.uncover", x)} disabled={readOnly} placeholder="Cover/uncover note" testid="cover-uncover" className="w-full" />
          </Section>
        )}

        {/* KRIMSKY */}
        {showBlock("krimsky") && (
          <Section title="Test di Krimsky" testid="mot-krimsky">
            <div className="grid grid-cols-2 gap-2">
              <Input value={v?.krimsky?.pl} onChange={(x) => set("krimsky.pl", x)} disabled={readOnly} placeholder="PL (Δ)" testid="krimsky-pl" />
              <Input value={v?.krimsky?.pv} onChange={(x) => set("krimsky.pv", x)} disabled={readOnly} placeholder="PV (Δ)" testid="krimsky-pv" />
            </div>
          </Section>
        )}

        {/* IRVINE */}
        {showBlock("irvine") && (
          <Section title="Test di Irvine" testid="mot-irvine">
            <textarea
              rows={2}
              disabled={readOnly}
              value={v?.irvine?.note || ""}
              onChange={(e) => set("irvine.note", e.target.value)}
              placeholder="Risultato test di Irvine…"
              className="w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs disabled:bg-slate-50"
              data-testid="irvine-note"
            />
          </Section>
        )}

        {/* GRIGLIA 9 POSIZIONI in diottrie prismatiche */}
        {showBlock("motilita_9_posizioni") && (
          <Section title="Griglia 9 posizioni di sguardo (Δ)" testid="mot-grid9">
            <div className="grid grid-cols-3 gap-1.5">
              {GRID9.map(([key, label]) => (
                <div key={key} className="flex flex-col">
                  <span className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">{label}</span>
                  <Input
                    value={getNested(v, `grid9.${key}`)}
                    onChange={(x) => set(`grid9.${key}`, x)}
                    disabled={readOnly}
                    placeholder="Δ"
                    testid={`grid9-${key}`}
                    className="w-full text-center"
                  />
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* VERSIONI / DUZIONI OD-OS */}
        {showBlock("versioni_duzioni") && (
          <Section title="Versioni / Duzioni OD-OS" testid="mot-versioni">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-slate-500">
                  <th className="text-left font-bold">Direzione</th>
                  <th className="text-center font-bold">OD</th>
                  <th className="text-center font-bold">OS</th>
                </tr>
              </thead>
              <tbody>
                {VERSION_DIRS.map((d) => (
                  <tr key={d.k}>
                    <td className="text-slate-700 font-bold py-1">{d.l}</td>
                    <td className="py-1">
                      <select
                        disabled={readOnly}
                        value={getNested(v, `versioni.${d.k}.OD`) || ""}
                        onChange={(e) => set(`versioni.${d.k}.OD`, e.target.value)}
                        data-testid={`versioni-${d.k}-OD`}
                        className="w-full px-1 py-0.5 rounded border border-[#E2E8F0] text-[11px] bg-white disabled:bg-slate-50"
                      >
                        <option value="">—</option>
                        {VERSION_VALUES.map((vv) => <option key={vv.v} value={vv.v}>{vv.l}</option>)}
                      </select>
                    </td>
                    <td className="py-1">
                      <select
                        disabled={readOnly}
                        value={getNested(v, `versioni.${d.k}.OS`) || ""}
                        onChange={(e) => set(`versioni.${d.k}.OS`, e.target.value)}
                        data-testid={`versioni-${d.k}-OS`}
                        className="w-full px-1 py-0.5 rounded border border-[#E2E8F0] text-[11px] bg-white disabled:bg-slate-50"
                      >
                        <option value="">—</option>
                        {VERSION_VALUES.map((vv) => <option key={vv.v} value={vv.v}>{vv.l}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {/* FORIE / TROPIE */}
        {showBlock("forie_tropie") && (
          <Section title="Forie / Tropie / AC-A" testid="mot-forie">
            <textarea
              rows={3}
              disabled={readOnly}
              value={v?.forie?.note || ""}
              onChange={(e) => set("forie.note", e.target.value)}
              placeholder="Foria latente vs tropia manifesta, angolo Δ, prisma cover, AC/A…"
              className="w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs disabled:bg-slate-50"
              data-testid="forie-note"
            />
          </Section>
        )}

        {/* CONVERGENZA */}
        {showBlock("convergenza") && (
          <Section title="Convergenza (PPC)" testid="mot-conv">
            <Input value={v?.convergenza?.ppc} onChange={(x) => set("convergenza.ppc", x)} disabled={readOnly} placeholder="PPC (cm)" testid="conv-ppc" className="w-full" />
            <Input value={v?.convergenza?.rottura} onChange={(x) => set("convergenza.rottura", x)} disabled={readOnly} placeholder="Rottura / Recupero" testid="conv-rott" className="w-full" />
          </Section>
        )}

        {/* ACCOMODAZIONE */}
        {showBlock("accomodazione") && (
          <Section title="Accomodazione (PPA)" testid="mot-acc">
            <Input value={v?.accomodazione?.ppa} onChange={(x) => set("accomodazione.ppa", x)} disabled={readOnly} placeholder="PPA (cm)" testid="acc-ppa" className="w-full" />
            <Input value={v?.accomodazione?.ampiezza} onChange={(x) => set("accomodazione.ampiezza", x)} disabled={readOnly} placeholder="Ampiezza (D)" testid="acc-amp" className="w-full" />
          </Section>
        )}

        {/* STEREOPSI */}
        {showBlock("stereopsi") && (
          <Section title="Stereopsi" testid="mot-stereo">
            <div className="grid grid-cols-2 gap-2">
              <Input value={v?.stereopsi?.titmus} onChange={(x) => set("stereopsi.titmus", x)} disabled={readOnly} placeholder="Titmus (sec arc)" testid="stereo-titmus" />
              <Input value={v?.stereopsi?.tno} onChange={(x) => set("stereopsi.tno", x)} disabled={readOnly} placeholder="TNO" testid="stereo-tno" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Lang II</div>
              <ChipGroup options={LANG_OPTIONS} value={v?.stereopsi?.lang_ii} onChange={(x) => set("stereopsi.lang_ii", x)} disabled={readOnly} testid="lang-chips" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Stereopsi ad angolo corretto</div>
              <ChipGroup options={STEREO_OPTIONS} value={v?.stereopsi?.angolo_corretto} onChange={(x) => set("stereopsi.angolo_corretto", x)} disabled={readOnly} testid="stereo-ang-chips" />
            </div>
          </Section>
        )}

        {/* LUCI WORTH */}
        {showBlock("luci_worth") && (
          <Section title="Luci di Worth" testid="mot-worth">
            <ChipGroup options={WORTH_OPTIONS} value={v?.worth?.risultato} onChange={(x) => set("worth.risultato", x)} disabled={readOnly} testid="worth-chips" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={v?.worth?.lontano} onChange={(x) => set("worth.lontano", x)} disabled={readOnly} placeholder="A 6 m" testid="worth-lont" />
              <Input value={v?.worth?.vicino} onChange={(x) => set("worth.vicino", x)} disabled={readOnly} placeholder="A 33 cm" testid="worth-vic" />
            </div>
          </Section>
        )}

        {/* VETRO ROSSO */}
        {showBlock("vetro_rosso") && (
          <Section title="Test del vetro rosso (diplopia)" testid="mot-vetro">
            <textarea
              rows={2}
              disabled={readOnly}
              value={v?.vetro_rosso?.note || ""}
              onChange={(e) => set("vetro_rosso.note", e.target.value)}
              placeholder="Posizione diplopia, separazione, omonima/incrociata, torsione…"
              className="w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs disabled:bg-slate-50"
              data-testid="vetro-note"
            />
          </Section>
        )}

        {/* BIELSCHOWSKY */}
        {showBlock("bielschowsky") && (
          <Section title="Head tilt test di Bielschowsky" testid="mot-biels">
            <div className="grid grid-cols-2 gap-2">
              <Input value={v?.bielschowsky?.dx} onChange={(x) => set("bielschowsky.dx", x)} disabled={readOnly} placeholder="Inclinazione DX" testid="biels-dx" />
              <Input value={v?.bielschowsky?.sx} onChange={(x) => set("bielschowsky.sx", x)} disabled={readOnly} placeholder="Inclinazione SX" testid="biels-sx" />
            </div>
            <Input value={v?.bielschowsky?.note} onChange={(x) => set("bielschowsky.note", x)} disabled={readOnly} placeholder="Note (muscolo coinvolto…)" testid="biels-note" className="w-full" />
          </Section>
        )}

        {/* DOLL HEAD */}
        {showBlock("doll_head") && (
          <Section title="Test della bambola / Doll Head" testid="mot-doll">
            <textarea
              rows={2}
              disabled={readOnly}
              value={v?.doll_head?.note || ""}
              onChange={(e) => set("doll_head.note", e.target.value)}
              placeholder="Risultato test, movimenti compensatori…"
              className="w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs disabled:bg-slate-50"
              data-testid="doll-note"
            />
          </Section>
        )}

        {/* POSIZIONE CAPO / PAC */}
        {showBlock("posizione_capo") && (
          <Section title="Posizione capo / Asimmetria facciale" testid="mot-pac">
            <Input value={v?.posizione_capo?.pac} onChange={(x) => set("posizione_capo.pac", x)} disabled={readOnly} placeholder="PAC / Inclinazione capo (gradi/lato)" testid="pac-input" className="w-full" />
            <Input value={v?.posizione_capo?.asimmetria} onChange={(x) => set("posizione_capo.asimmetria", x)} disabled={readOnly} placeholder="Asimmetria facciale" testid="pac-asim" className="w-full" />
          </Section>
        )}

        {/* SCHERMO HESS / LANCASTER */}
        {showBlock("schermo_hess") && (
          <Section title="Schermo di Hess / Lancaster" testid="mot-hess">
            <textarea
              rows={3}
              disabled={readOnly}
              value={v?.hess?.note || ""}
              onChange={(e) => set("hess.note", e.target.value)}
              placeholder="Sintesi grafica / quadro di deviazione per ciascun occhio nelle 9 posizioni"
              className="w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs disabled:bg-slate-50"
              data-testid="hess-note"
            />
          </Section>
        )}

        {/* FOTO GAZE9 */}
        {showBlock("gaze9_foto") && (
          <Section title="Foto Gaze9 (9 posizioni di sguardo)" testid="mot-gaze9">
            <div className="flex items-center gap-2 flex-wrap">
              {!readOnly && (
                <label className="inline-flex items-center gap-1 text-[11px] text-brand-azure hover:underline cursor-pointer font-bold">
                  <Upload size={12} /> Carica foto
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="gaze9-upload"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f && onAttachGaze9) onAttachGaze9(f); e.target.value = ""; }}
                  />
                </label>
              )}
              {Array.isArray(gaze9Files) && gaze9Files.length > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] text-slate-600" data-testid="gaze9-files">
                  <ImageIcon size={11} /> {gaze9Files.length} foto allegate
                </span>
              )}
            </div>
            <textarea
              rows={2}
              disabled={readOnly}
              value={v?.gaze9?.descrizione || ""}
              onChange={(e) => set("gaze9.descrizione", e.target.value)}
              placeholder="Descrizione foto Gaze9 / IA preliminare…"
              className="w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs disabled:bg-slate-50"
              data-testid="gaze9-note"
            />
          </Section>
        )}

        {/* DIPLOPIA */}
        {showBlock("diplopia") && (
          <Section title="Diplopia" testid="mot-diplopia">
            <textarea
              rows={2}
              disabled={readOnly}
              value={v?.diplopia?.note || ""}
              onChange={(e) => set("diplopia.note", e.target.value)}
              placeholder="Mono/binoculare, orizzontale/verticale/torsionale, intermittente/costante…"
              className="w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs disabled:bg-slate-50"
              data-testid="diplopia-note"
            />
          </Section>
        )}

        {/* NISTAGMO */}
        {showBlock("nistagmo") && (
          <Section title="Nistagmo" testid="mot-nistagmo">
            <textarea
              rows={2}
              disabled={readOnly}
              value={v?.nistagmo?.note || ""}
              onChange={(e) => set("nistagmo.note", e.target.value)}
              placeholder="Tipo (pendolare/scossa), direzione, ampiezza, frequenza, posizione di blocco…"
              className="w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs disabled:bg-slate-50"
              data-testid="nistagmo-note"
            />
          </Section>
        )}

        {/* PTOSI */}
        {showBlock("ptosi") && (
          <Section title="Ptosi / Posizione palpebrale" testid="mot-ptosi">
            <div className="grid grid-cols-2 gap-2">
              <Input value={v?.ptosi?.mrd1_od} onChange={(x) => set("ptosi.mrd1_od", x)} disabled={readOnly} placeholder="MRD1 OD" testid="ptosi-mrd1-od" />
              <Input value={v?.ptosi?.mrd1_os} onChange={(x) => set("ptosi.mrd1_os", x)} disabled={readOnly} placeholder="MRD1 OS" testid="ptosi-mrd1-os" />
              <Input value={v?.ptosi?.mrd2_od} onChange={(x) => set("ptosi.mrd2_od", x)} disabled={readOnly} placeholder="MRD2 OD" testid="ptosi-mrd2-od" />
              <Input value={v?.ptosi?.mrd2_os} onChange={(x) => set("ptosi.mrd2_os", x)} disabled={readOnly} placeholder="MRD2 OS" testid="ptosi-mrd2-os" />
            </div>
            <Input value={v?.ptosi?.escursione} onChange={(x) => set("ptosi.escursione", x)} disabled={readOnly} placeholder="Escursione levatore" testid="ptosi-esc" className="w-full" />
          </Section>
        )}

        {/* TORSIONE */}
        {showBlock("torsione") && (
          <Section title="Torsione oculare (fundus)" testid="mot-torsione">
            <textarea
              rows={2}
              disabled={readOnly}
              value={v?.torsione?.note || ""}
              onChange={(e) => set("torsione.note", e.target.value)}
              placeholder="Eccicloduzione / incicloduzione, fundus disco rotato…"
              className="w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs disabled:bg-slate-50"
              data-testid="torsione-note"
            />
          </Section>
        )}
      </div>
    </div>
  );
}
